import Analytics, { ExperimentParameter, StatsigExperiment } from '@opencare/analytics';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { UserMarketName } from 'connect/saga/handlers/user/user-market-name';

import { selectDataUser } from 'connect/selectors';
import { useHookResult } from 'utils/customHooks/use-hook-result';
import Logger from 'utils/Logger';

const analytics = new Analytics();

export interface ExperimentParameterInfo<TName extends string = string, TValue extends ExperimentParameter = unknown> {
  name: TName;
  defaultValue: TValue;
}

/**
 * Some magic to infer names and value types from the given parameter info
 */
type EvaluatedParameters<TParameterInfo extends readonly ExperimentParameterInfo[]> = {
  [K in TParameterInfo[number] as K['name']]: K['defaultValue'];
};

interface UseStatsigExperimentOptions<TParameterInfo extends readonly ExperimentParameterInfo[]> {
  /**
   * Properties needed to determine if the user is in the experiment
   * IMPORTANT: This should be memoized, stored in state, or be a constant value so it doesn't change every time the hook is called
   */
  properties?: Record<string, string>;

  /**
   * Information about the parameters that the experiment will provide.
   * The hook will return an object with the names and values of the parameters.
   * IMPORTANT: This should be memoized, stored in state, or be a constant value so it doesn't change every time the hook is called
   * @example
   * const PARAMETER_INFO = [
   *   {
   *     name: 'title_text',
   *
   *     // The type of the value will be inferred from the defaultValue
   *     // If we don't have `as string` then the type will be the constant type `'Welcome to Opencare!'`
   *     // This won't matter in most cases but is important to note for the type inference.
   *     defaultValue: 'Welcome to Opencare!' as string,
   *   },
   * ] as const; // This is important to infer the types correctly
   *
   * const { experimentParameters } = useStatsigExperiment('test_experiment', {
   *   parameterInfo: PARAMETER_INFO,
   * });
   *
   * experimentParameters.title_text; // Title text will have the type `string`
   */
  parameterInfo?: TParameterInfo;

  /**
   * IMPORTANT: This should be used sparingly, most conditions should be handled by Statsig by adding the condition to the experiment properties.
   * If the condition is false, the experiment will not be loaded.
   * This is useful for experiments that require certain conditions.
   *
   * @example
   * const { experimentParameters } = useStatsigExperiment('signup_experiment', {
   *   condition: !isLoggedIn, // If the user is not logged in, the experiment will not be loaded or tracked.
   * });
   */
  condition?: boolean;
}

const evaluateParameters = <const TParameterInfo extends readonly ExperimentParameterInfo[]>(
  parameterInfo: TParameterInfo,
  experiment: StatsigExperiment | null,
) => {
  return parameterInfo.reduce((acc, parameter) => {
    (acc as Record<string, unknown>)[parameter.name] =
      experiment?.get(parameter.name, parameter.defaultValue) ?? parameter.defaultValue;
    return acc;
  }, {} as EvaluatedParameters<TParameterInfo>);
};

export const useStatsigExperiment = <const TParameterInfo extends readonly ExperimentParameterInfo[]>(
  experimentName: string,
  options: UseStatsigExperimentOptions<TParameterInfo> = {},
) => {
  const userMarket = useSelector(UserMarketName.value!.selector) as string | undefined;
  const user = useSelector(selectDataUser);

  useEffect(() => {
    if (user?.id) {
      analytics.setUserId(user?.id);
    }
  }, [user]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);

  const [experimentParameters, setExperimentParameters] = useState<EvaluatedParameters<TParameterInfo>>(
    evaluateParameters(options.parameterInfo ?? [], null),
  );

  const properties = useMemo(
    () => ({
      ...options.properties ?? {},
      user_market: userMarket,
    }),
    [options.properties, userMarket],
  );

  const onExperimentLoaded = useCallback((experiment: StatsigExperiment) => {
    setExperimentParameters(evaluateParameters(options.parameterInfo ?? [], experiment));

    if (!experiment.groupName) {
      return;
    }

    analytics.trackPageView({
      pageType: analytics.pageTypes.EXPERIMENT_VARIANT_ASSIGNED,
      pageName: 'experiment-variant-assigned',
      experimentName: String(experiment.name),
      experimentGroup: experiment.groupName,
      noTrackCro: true,
    });
  }, [options.parameterInfo]);

  useEffect(() => {
    const isConditionSet = typeof options.condition === 'boolean';
    if (isConditionSet && !options.condition) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    (async () => {
      try {
        const experiment = await analytics.getExperimentStatus(experimentName, properties);
        if (!experiment) {
          throw new Error(`Could not find experiment ${experimentName}`);
        }

        onExperimentLoaded(experiment);
      } catch (error) {
        setError(error);
        Logger.error('Error getting experiment from statsig', error, {
          experimentName,
          properties,
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [experimentName, properties, onExperimentLoaded, options.condition]);

  // We are ready if the experiment is loaded or there is an error
  // We don't want to block the user because an experiment failed to load
  // we'll just log the error and let the user continue with the default values
  const isReady = useMemo(() => !isLoading || error, [isLoading, error]);

  return useHookResult({
    experimentParameters,
    isReady,
  });
};
